import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import { KIND_LABELS } from '../../../utils/emailPlates';
import EmailPlateStrip from './EmailPlateStrip';

// Screen 1 of the approved design: the campaigns table, drawn in the CMS's own
// .ps-card / .ps-table materials (src/styles/_table.scss) so it reads as the
// same furniture as the other list tabs.
//
// A campaign's scope word does two jobs at once — it is the flag written on the
// Braze profile and the key that finds the email — which is why the key, not the
// title, is the loud thing in the first column.
//
// The design also carries a Braze-flag column and a "Last edited · author"
// column. getEmailCampaigns returns neither (see task-10-report.md), so those
// two columns are left out rather than filled with invented data; the template
// count, which the query does return, takes the last column instead.
const EmailCampaignList = ({ campaigns, languages, totalCount }) => (
  <div className="ps-card">
    <table className="ps-table">
      <thead>
        <tr>
          <th style={{ width: '42%' }}>Campaign</th>
          <th style={{ width: '14%' }}>Kind</th>
          <th style={{ width: '10%' }}>Active</th>
          <th style={{ width: '20%' }}>Plates</th>
          <th style={{ width: '14%' }}>Templates</th>
        </tr>
      </thead>
      <tbody>
        {campaigns.map(campaign => (
          <tr key={campaign.key} className={campaign.isActive === false ? 'dormant' : undefined}>
            {/*
              The campaign name is a link because the plate chips are not enough:
              a campaign with no templates renders "generic email" instead of
              chips, so most of this list had no way in at all — not even to write
              its first plate. `en` is DEFAULT_LANGUAGE, and the destination
              handles a missing template by offering to create it.
            */}
            <td>
              <Link to={`/cms/emails/${campaign.key}/en`} className="campaign-link">
                <span className="scope-key">{campaign.key}</span>
                <span className="scope-title">{campaign.title}</span>
              </Link>
            </td>
            <td>
              <span className={`kind ${campaign.kind}`}>
                {KIND_LABELS[campaign.kind] || campaign.kind}
              </span>
            </td>
            <td>
              <span
                className={campaign.isActive ? 'sw on' : 'sw'}
                role="img"
                aria-label={campaign.isActive ? 'Active' : 'Inactive'}
                title={campaign.isActive ? 'Active' : 'Inactive'}
              />
            </td>
            <td>
              <EmailPlateStrip campaign={campaign} languages={languages} />
            </td>
            {/*
              The count is ACTIVE plates. A switched-off plate is called out beside
              it rather than folded into it: the plate strip already shows them, but
              a list-kind campaign renders the generic marker instead of a strip, so
              without this its off plates would have nowhere to appear at all.
            */}
            <td className="num">
              {campaign.templateCount || 0}
              {campaign.inactiveCount > 0 && (
                <span className="offcount" title={`${campaign.inactiveCount} plate(s) exist here but are switched off`}>
                  {`+${campaign.inactiveCount} off`}
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    <div className="tablefoot">
      {`${campaigns.length} of ${totalCount} · ${campaigns.filter(c => c.kind === 'campaign').length} campaigns`}
      {` · ${campaigns.filter(c => c.kind === 'system').length} system`}
      {` · ${campaigns.filter(c => c.kind === 'list').length} lists`}
      {` · ${campaigns.reduce((total, c) => total + (c.templateCount || 0), 0)} templates`}
    </div>
  </div>
);

EmailCampaignList.propTypes = {
  campaigns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    title: PropTypes.string,
    kind: PropTypes.string,
    isActive: PropTypes.bool,
    templateCount: PropTypes.number,
    inactiveCount: PropTypes.number,
    hasAnyTemplate: PropTypes.bool,
    languages: PropTypes.arrayOf(PropTypes.shape({
      language: PropTypes.string,
      status: PropTypes.string,
    })),
  })).isRequired,
  languages: PropTypes.arrayOf(PropTypes.string).isRequired,
  totalCount: PropTypes.number.isRequired,
};

export default EmailCampaignList;
